import { create, deepClone } from 'smbls'
import { convertSvgToSymbol } from '@symbo.ls/scratch'

const NATIVE_RE = /\{\s*\[native code\]\s*\}/
const GLOBAL_REF_RE = /\b(window|document)\s*\./

export function rerealmFunctions (node, frameWindow, seen = new WeakSet()) {
  if (!node || typeof node !== 'object') return
  if (seen.has(node)) return
  seen.add(node)
  for (const key in node) {
    if (!Object.prototype.hasOwnProperty.call(node, key)) continue
    const v = node[key]
    if (typeof v === 'function') {
      if (v.__rerealmed) continue
      let src
      try { src = Function.prototype.toString.call(v) } catch { continue }
      if (NATIVE_RE.test(src) || !GLOBAL_REF_RE.test(src)) continue
      let reFn
      try { reFn = frameWindow.eval('(' + src + ')') } catch { continue }
      const wrapped = function (...args) {
        try { return reFn.apply(this, args) } catch (e) {
          if (e instanceof ReferenceError) return v.apply(this, args)
          throw e
        }
      }
      wrapped.__rerealmed = true
      node[key] = wrapped
    } else if (v && typeof v === 'object') {
      rerealmFunctions(v, frameWindow, seen)
    }
  }
}

export function injectSprites (doc, designSystem) {
  if (!doc?.body || !designSystem) return
  let spriteHtml = ''
  if (designSystem.icons) {
    for (const key in designSystem.icons)
      spriteHtml += convertSvgToSymbol(key, designSystem.icons[key])
  }
  if (designSystem.svg) {
    for (const key in designSystem.svg)
      spriteHtml += convertSvgToSymbol(key, designSystem.svg[key])
  }
  if (spriteHtml) {
    doc.body.insertAdjacentHTML(
      'afterbegin',
      `<svg aria-hidden="true" width="0" height="0" style="position:absolute" id="svgSprite">${spriteHtml}</svg>`
    )
  }
}

export async function resetIframeDocument (iframeNode) {
  // A freshly-mounted iframe (no explicit `src` OR still on about:blank)
  // already has its contentDocument ready and `readyState: 'complete'`.
  // Listening for a `load` event after that point hangs forever —
  // canvas-side tile frames hit this because DOMQL creates the <iframe>
  // synchronously before our handler attaches. Only do the reload dance
  // when the iframe is currently pointed somewhere else.
  const currentSrc = iframeNode.getAttribute('src') || ''
  const needsReload = currentSrc && currentSrc !== 'about:blank'
  if (needsReload) {
    iframeNode.src = 'about:blank'
    await new Promise(resolve => {
      iframeNode.addEventListener('load', resolve, { once: true })
    })
  }
  const frameWindow = iframeNode.contentWindow
  const doc = iframeNode.contentDocument || frameWindow.document
  doc.open()
  doc.write('<!DOCTYPE html><html><head><meta name="color-scheme" content="light dark"><style>*{margin:0;padding:0;box-sizing:border-box}html,body{width:100%;height:100%;overflow:auto}</style></head><body></body></html>')
  doc.close()
  return { frameWindow, doc }
}

// Shared bootstrap for mounting any smbls app inside a preview iframe.
// Handles realm-safe cloning, re-realming of project functions, sprite
// injection, create(), and theme resolution. Returns the create() result.
export async function createIframeApp (iframeNode, data, shell, {
  path = '/',
  contextOverrides = {},
  resetDocument = true
} = {}) {
  if (!iframeNode || !data) return null

  let frameWindow, doc
  if (resetDocument) {
    ({ frameWindow, doc } = await resetIframeDocument(iframeNode))
  } else {
    frameWindow = iframeNode.contentWindow
    doc = iframeNode.contentDocument || frameWindow.document
  }
  if (!frameWindow) return null

  try { frameWindow.history.replaceState(null, '', path) } catch {}

  const hasRealFunctions = typeof data.app?.onRender === 'function'
  const project = hasRealFunctions ? data : deepClone(data, { window: frameWindow })
  rerealmFunctions(project, frameWindow)

  injectSprites(doc, project.designSystem)

  const result = await create(shell, {
    ...project,
    ...contextOverrides,
    themeRoot: doc.documentElement,
    document: doc,
    window: frameWindow,
    parent: doc.body
  })

  const appEl = doc.querySelector('[data-smbls-app]') || doc.documentElement
  let theme = data.globalTheme || 'auto'
  if (theme === 'auto') {
    theme = frameWindow.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  appEl.setAttribute('data-theme', theme)
  if (doc.documentElement !== appEl) doc.documentElement.removeAttribute('data-theme')

  if (result?.context?.designSystem) {
    result.context.designSystem.themeRoot = appEl
    iframeNode.__projectConfig = result.context.designSystem
  }

  return result
}
